import { useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { timeAgo } from "@/lib/time"
import {
  inviteWaitlistEntry,
  type WaitlistEntry,
} from "@/lib/api/admin-waitlist"

export function WaitlistEntryRow({ entry }: { entry: WaitlistEntry }) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const { mutate, isPending } = useMutation({
    mutationFn: () => inviteWaitlistEntry(entry.id),
    onSuccess: (updated) => {
      queryClient.setQueryData<WaitlistEntry[]>(["admin", "waitlist"], (prev) =>
        prev?.map((e) => (e.id === updated.id ? updated : e))
      )
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-foreground">{entry.name}</span>
              {entry.invitedAt ? (
                <span className="rounded-full bg-success-soft px-2 py-0.5 text-xs font-medium text-success-deep">
                  Invited {timeAgo(entry.invitedAt)}
                </span>
              ) : (
                <span className="rounded-full bg-destructive-soft px-2 py-0.5 text-xs font-medium text-destructive-deep dark:bg-destructive-soft dark:text-destructive">
                  Pending
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{entry.email}</p>
            <p className="mt-1 text-sm text-foreground/80">{entry.reason}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Joined {timeAgo(entry.createdAt)}
            </p>
            {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
          </div>
          {!entry.invitedAt && (
            <Button
              size="sm"
              onClick={() => {
                setError(null)
                mutate()
              }}
              disabled={isPending}
            >
              {isPending ? <Spinner className="mr-2 h-3 w-3" /> : null}
              Send Invite
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
